import { useEffect, useRef, useState } from "react";
import { confirmMatches } from "../lib/confirmDelete";
import { t, tVars } from "../i18n";

interface Props {
  open: boolean;
  itemKind: "transcript" | "project" | "user";
  itemName: string;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * The one heavy guard in the Archive flow. Lives only inside the Archive view.
 * Type-to-confirm: the delete button stays disabled until the typed name
 * exactly matches. Honest, plain-language copy about what is purged.
 */
export function ConfirmPermanentDelete({
  open,
  itemKind,
  itemName,
  busy,
  onConfirm,
  onCancel,
}: Props) {
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const [typed, setTyped] = useState("");
  const [shownFor, setShownFor] = useState(itemName);

  // Reset the typed guard SYNCHRONOUSLY when the target changes (incl. open/
  // close, since itemName is "" when closed). Doing this in render — not a
  // post-paint effect — closes the one-frame window where a stale typed value
  // from a previous target could match a different, same-named item and
  // briefly enable the irreversible Delete button.
  if (itemName !== shownFor) {
    setShownFor(itemName);
    setTyped("");
  }

  useEffect(() => {
    const dlg = dialogRef.current;
    if (!dlg) return;
    if (open && !dlg.open) dlg.showModal();
    if (!open && dlg.open) dlg.close();
  }, [open]);

  const canDelete = confirmMatches(typed, itemName) && !busy;

  const purgeLine =
    itemKind === "transcript"
      ? t("confirmDelete.transcriptMessage")
      : itemKind === "project"
      ? t("confirmDelete.projectMessage")
      : t("confirmDelete.userMessage");

  return (
    <dialog
      ref={dialogRef}
      onClose={onCancel}
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
      style={{
        border: "1px solid var(--border)",
        borderRadius: 8,
        background: "var(--surface)",
        color: "var(--text)",
        padding: 20,
        minWidth: 420,
        maxWidth: "min(560px, 90vw)",
      }}
    >
      <h3 style={{ marginTop: 0 }}>{t("confirmDelete.title")}</h3>
      <p style={{ fontSize: 14 }}>{purgeLine}</p>
      <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 6 }}>
        {tVars("confirmDelete.prompt", {
          kind:
            itemKind === "transcript"
              ? t("confirmDelete.kindTitle")
              : itemKind === "project"
              ? t("confirmDelete.kindProjectName")
              : t("confirmDelete.kindEmail"),
        })}{" "}
        <strong dir="auto">{itemName}</strong>
      </p>
      <input
        type="text"
        dir="auto"
        autoFocus
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
        disabled={busy}
        style={{ width: "100%", boxSizing: "border-box" }}
        placeholder={itemName}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
          marginTop: 16,
        }}
      >
        <button
          type="button"
          className="secondary"
          onClick={onCancel}
          disabled={busy}
        >
          {t("common.cancel")}
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={!canDelete}
          style={{
            background: canDelete ? "#b00020" : undefined,
            color: canDelete ? "#fff" : undefined,
          }}
        >
          {busy ? t("confirmDelete.deleting") : t("library.deletePermanently")}
        </button>
      </div>
    </dialog>
  );
}
